// utils/richtext.ts

export interface RichTextNode {
  type: string
  content?: RichTextNode[]
  text?: string
  marks?: Array<{
    type: string
    attrs?: Record<string, any>
  }>
  attrs?: Record<string, any>
}

export interface RichTextDocument {
  type: string
  content: RichTextNode[]
}

/**
 * Extrahiert Plain Text aus Storyblok Rich Text
 */
export function extractPlainText(richtext: any): string {
  if (!richtext) return ''
  
  // Wenn es bereits ein String ist
  if (typeof richtext === 'string') return richtext
  
  // Wenn es ein RichText-Objekt ist
  if (richtext.content && Array.isArray(richtext.content)) {
    return richtext.content
      .map((node: RichTextNode) => extractTextFromNode(node))
      .filter((text: string) => text.trim().length > 0)
      .join(' ')
  }
  
  return ''
}

/**
 * Extrahiert Text aus einem einzelnen Node
 */
function extractTextFromNode(node: RichTextNode): string {
  if (!node) return ''
  
  if (node.type === 'text') {
    return node.text || ''
  }
  
  if (node.content && Array.isArray(node.content)) {
    return node.content
      .map(child => extractTextFromNode(child))
      .join('')
  }
  
  return ''
}

export interface RenderOptions {
  linkTarget?: string
  linkClass?: string
  imageClass?: string
  headingClasses?: Record<number, string>
  paragraphClass?: string
}

/**
 * Konvertiert Storyblok Rich Text zu HTML
 */
export function renderRichText2(richtext: any, options: RenderOptions = {}): string {
  if (!richtext) return ''
  
  // Wenn es bereits ein String ist
  if (typeof richtext === 'string') return richtext
  
  // Wenn es ein RichText-Objekt ist
  if (richtext.content && Array.isArray(richtext.content)) {
    return richtext.content
      .map((node: RichTextNode) => renderNode(node, options))
      .filter((html: string) => html.trim().length > 0)
      .join('')
  }
  
  return ''
}

/**
 * Rendert einen einzelnen Node zu HTML
 */
function renderNode(node: RichTextNode, options: RenderOptions = {}): string {
  if (!node) return ''
  
  switch (node.type) {
    case 'doc':
      return node.content?.map(child => renderNode(child, options)).join('') || ''
    
    case 'paragraph':
      const pContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      const pClass = options.paragraphClass ? ` class="${options.paragraphClass}"` : ''
      return pContent ? `<p${pClass}>${pContent}</p>` : ''
    
    case 'heading':
      const level = node.attrs?.level || 2
      const hContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      const hClass = options.headingClasses?.[level] ? ` class="${options.headingClasses[level]}"` : ''
      return hContent ? `<h${level}${hClass}>${hContent}</h${level}>` : ''
    
    case 'text':
      let text = node.text || ''
      
      // Formatierung anwenden
      if (node.marks) {
        node.marks.forEach(mark => {
          switch (mark.type) {
            case 'strong':
            case 'bold':
              text = `<strong>${text}</strong>`
              break
            case 'em':
            case 'italic':
              text = `<em>${text}</em>`
              break
            case 'underline':
              text = `<u>${text}</u>`
              break
            case 'strike':
              text = `<s>${text}</s>`
              break
            case 'code':
              text = `<code>${text}</code>`
              break
            case 'link':
              const href = mark.attrs?.href || '#'
              const target = mark.attrs?.target || options.linkTarget || '_self'
              const linkClass = options.linkClass ? ` class="${options.linkClass}"` : ''
              const targetAttr = target !== '_self' ? ` target="${target}"` : ''
              text = `<a href="${href}"${targetAttr}${linkClass}>${text}</a>`
              break
          }
        })
      }
      
      return text
    
    case 'hard_break':
    case 'break':
      return '<br>'
    
    case 'horizontal_rule':
      return '<hr>'
    
    case 'blockquote':
      const bqContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      return bqContent ? `<blockquote>${bqContent}</blockquote>` : ''
    
    case 'code_block':
      const codeContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      const language = node.attrs?.language || ''
      const langClass = language ? ` class="language-${language}"` : ''
      return codeContent ? `<pre><code${langClass}>${codeContent}</code></pre>` : ''
    
    case 'bullet_list':
      const ulContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      return ulContent ? `<ul>${ulContent}</ul>` : ''
    
    case 'ordered_list':
      const olContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      return olContent ? `<ol>${olContent}</ol>` : ''
    
    case 'list_item':
      const liContent = node.content?.map(child => renderNode(child, options)).join('') || ''
      return liContent ? `<li>${liContent}</li>` : ''
    
    case 'image':
      const src = node.attrs?.src || ''
      const alt = node.attrs?.alt || ''
      const title = node.attrs?.title || ''
      const imgClass = options.imageClass ? ` class="${options.imageClass}"` : ''
      const titleAttr = title ? ` title="${title}"` : ''
      return src ? `<img src="${src}" alt="${alt}"${titleAttr}${imgClass}>` : ''
    
    default:
      // Fallback für unbekannte Node-Typen
      if (node.content) {
        return node.content.map(child => renderNode(child, options)).join('')
      }
      return ''
  }
}

/**
 * Erstellt einen Excerpt aus Rich Text
 */
export function createExcerpt(richtext: any, maxLength: number = 150): string {
  const plainText = extractPlainText(richtext)
  
  if (plainText.length <= maxLength) {
    return plainText
  }
  
  // Schneide bei einem Wort ab, nicht mitten im Wort
  const truncated = plainText.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  
  if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + '...'
  }
  
  return truncated + '...'
}

/**
 * Prüft ob Rich Text leer ist
 */
export function isRichTextEmpty(richtext: any): boolean {
  const text = extractPlainText(richtext)
  return text.trim().length === 0
}

/**
 * Zählt Wörter in Rich Text
 */
export function countWords(richtext: any): number {
  const text = extractPlainText(richtext)
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}

/**
 * Schätzt Lesezeit basierend auf Rich Text (200 WPM)
 */
export function estimateReadingTime(richtext: any, wordsPerMinute: number = 200): number {
  const wordCount = countWords(richtext)
  return Math.ceil(wordCount / wordsPerMinute)
}

// Vorgefertigte Render-Optionen
export const defaultRenderOptions: RenderOptions = {
  linkTarget: '_blank',
  linkClass: 'text-blue-600 hover:text-blue-800 underline',
  imageClass: 'max-w-full h-auto',
  paragraphClass: 'mb-4',
  headingClasses: {
    1: 'text-4xl font-bold mb-6',
    2: 'text-3xl font-bold mb-5',
    3: 'text-2xl font-bold mb-4',
    4: 'text-xl font-bold mb-3',
    5: 'text-lg font-bold mb-2',
    6: 'text-base font-bold mb-2'
  }
}

export const blogRenderOptions: RenderOptions = {
  linkTarget: '_blank',
  linkClass: 'text-blue-600 hover:text-blue-800 underline transition-colors',
  imageClass: 'w-full h-auto rounded-lg shadow-lg my-6',
  paragraphClass: 'mb-4 leading-relaxed',
  headingClasses: {
    1: 'text-4xl font-bold mb-6 text-gray-900',
    2: 'text-3xl font-bold mb-5 text-gray-900',
    3: 'text-2xl font-bold mb-4 text-gray-900',
    4: 'text-xl font-semibold mb-3 text-gray-800',
    5: 'text-lg font-semibold mb-2 text-gray-800',
    6: 'text-base font-semibold mb-2 text-gray-800'
  }
}

// Default Export für bessere Kompatibilität
export default {
  extractPlainText,
  renderRichText,
  createExcerpt,
  isRichTextEmpty,
  countWords,
  estimateReadingTime,
  defaultRenderOptions,
  blogRenderOptions
}